<template>
  <div class="organism product-detail-brand-window bg-gray-50">
    <div
      :class="[
        'relative mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-[minmax(300px,_35%)_minmax(300px,_65%)]',
        processingQueue ? 'cursor-wait' : '',
      ]"
    >
      <button
        v-if="organism.hasFavorites"
        @click.stop.prevent="
          handleFavoriteClickProductDetailBW({
            id: organism.id,
            title: organism.title,
            thumbnail: organism.thumbnail,
          })
        "
        class="absolute z-10 w-12 right-4 top-4"
        :title="isFavorite ? 'Remove from Favorites' : 'Add to Favorites'"
      >
        <HeartIconSolid v-if="isFavorite" :class="['text-heart-red']" />
        <HeartIconOutline
          v-else
          :class="['text-heart-red hover:fill-heart-red/75']"
        />
      </button>
      <div
        id="product-detail-brand-window-text-content"
        class="max-w-md px-4 py-12 mx-auto"
      >
        <AtomsCollectionButton :collection="organism.collection" />
        <span
          class="text-sm font-figtree font-semibold text-black self-center inline-block px-3 py-1 mb-4 text-black bg-white rounded-full drop-shadow ml-1"
          v-for="(addType, index) in organism.addType"
          v-if="organism.addType"
          :key="index"
        >
          {{ addType }}
        </span>
        <ClientOnly>
          <h1
            v-if="organism.title"
            id="product-detail-brand-window-title"
            class="mb-3 text-5xl font-semibold text-gray-900 font-figtree"
          >
            {{ organism.title }}
            <br />
            <span class="inline text-primary-500">{{ organism.type }}</span>
          </h1>
          <div
            v-for="(neighborhood, index) in organism.neighborhoods"
            :key="index"
          >
            <NuxtLink
              class="block mb-4 font-medium uppercase text-bc-dark-blue font-figtree tracking-[0.71px]"
              :to="neighborhood.neighborhoodLink"
            >
              {{ neighborhood.neighborhoodTitle }}&nbsp;»
            </NuxtLink>
          </div>
          <NuxtLink
            v-if="organism.pageType === 'qmi-detail'"
            class="uppercase font-figtree font-medium text-[15px] block mb-6"
            :to="
              'https://www.google.com/maps/place/' +
              organism.latitude +
              ',' +
              organism.longitude
            "
            target="_blank"
          >
            <svg
              class="inline w-5 h-5 text-primary-500"
              viewBox="-10 0 500 500"
              width="500"
              height="500"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="currentColor"
                d="M220.187 476.912c-.961-1.146-1.951-2.33-2.968-3.548 1.345-1.542 2.846-3.272 4.49-5.182a1469.015 1469.015 0 0 0 20.609-24.502c21.355-25.936 42.707-53.451 62.577-81.266 16.086-22.518 30.498-44.272 42.804-64.86 28.354-47.435 43.859-86.23 43.859-110.053 0-84.442-67.907-152.802-151.557-152.802-83.651 0-151.558 68.36-151.558 152.802 0 23.824 15.505 62.619 43.859 110.053 12.306 20.588 26.718 42.342 42.804 64.86 19.87 27.815 41.223 55.33 62.577 81.266a1465.184 1465.184 0 0 0 20.609 24.502c1.644 1.91 3.144 3.64 4.49 5.182a1859.136 1859.136 0 0 1-2.968 3.548h-39.629.001zM240 500S53.745 291.053 53.745 187.5 137.135 0 240 0c102.866 0 186.255 83.947 186.255 187.5S240 500 240 500Zm0-250c-34.289 0-62.084-27.982-62.084-62.5S205.71 125 240 125s62.085 27.982 62.085 62.5S274.289 250 240 250z"
                style="stroke-width: 0.867432"
              />
            </svg>
            {{ organism.address1
            }}<span v-if="organism.address2">, {{ organism.address2 }}</span>
            <span v-else>
              {{ organism.city }},
              {{ organism.state }}
              {{ organism.zip }}
            </span>
          </NuxtLink>
          <ul class="mb-7">
            <li v-if="organism.bedsMin || organism.bedsMax" class="flex gap-4">
              <div><i class="text-4xl icon icon-beds"></i></div>
              <div
                class="flex gap-x-2 items-center text font-opensans tracking-[1.32px] flex-wrap"
              >
                <span class="font-bold">{{
                  detailMinMax(organism.bedsMin, organism.bedsMax, false)
                }}</span>
                <span class="uppercase">Bedrooms</span>
              </div>
            </li>
            <li
              v-if="organism.bathsMin || organism.bathsMax"
              class="flex gap-4"
            >
              <div><i class="text-4xl icon icon-baths"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span class="font-bold">{{
                  detailMinMax(organism.bathsMin, organism.bathsMax, false)
                }}</span>
                <span class="uppercase">Bathrooms</span>
              </div>
            </li>
            <li
              v-if="organism.garageMin || organism.garageMax"
              class="flex gap-4"
            >
              <div><i class="text-4xl icon icon-garage"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span class="font-bold">{{
                  detailMinMax(organism.garageMin, organism.garageMax, false)
                }}</span>
                <span class="uppercase">CAR GARAGE</span>
              </div>
            </li>
            <li v-if="organism.sqftMin" class="flex gap-4">
              <div><i class="text-4xl icon icon-sq-ft-total"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span class="font-bold">{{
                  organism?.sqftMin?.toLocaleString("en-US")
                }}</span>
                <span>SQ. FT</span>
              </div>
            </li>
            <li v-if="organism.sqftMax" class="flex gap-4">
              <div><i class="text-4xl icon icon-sq-ft-main-floor"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span class="uppercase">Total</span>
                <span class="font-bold">
                  {{ organism?.sqftMax?.toLocaleString("en-US") }} SQ. FT
                </span>
                <AtomsToolTipButton
                  class="relative tracking-normal uppercase -top-2"
                >
                  Square footage varies based on floorplan options selected.
                </AtomsToolTipButton>
              </div>
            </li>
            <li v-if="organism.sqftFinished" class="flex gap-4">
              <div><i class="text-4xl icon icon-sq-ft-total"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span class="uppercase">Total</span>
                <span class="font-bold"
                  >{{ organism?.sqftFinished?.toLocaleString("en-US") }} SQ.
                  FT</span
                >
              </div>
            </li>
            <li
              v-if="
                getRoundedPrice(organism.priceMin, 1000) ||
                organism.pageType === 'qmi-detail'
              "
              class="flex gap-4 productDetailBWPrice"
            >
              <div><i class="text-4xl icon icon-price"></i></div>
              <div
                class="flex flex-wrap gap-x-2 items-center text font-opensans tracking-[1.32px]"
              >
                <span
                  v-if="organism.pageType === 'qmi-detail'"
                  class="uppercase"
                >
                  <span>Priced at </span>
                  <span class="font-bold"
                    >${{ organism.price?.toLocaleString("en-US") }}</span
                  >
                </span>
                <span v-else class="uppercase">
                  <span>Priced at </span>
                  <span class="font-bold">
                    ${{ organism.priceMin?.toLocaleString("en-US") }}
                  </span>
                </span>

                <AtomsToolTipButton
                  class="relative tracking-normal uppercase -top-2"
                  >Final price depends on homesite and options
                  selected.</AtomsToolTipButton
                >
              </div>
            </li>
          </ul>
          <MoleculesButtons
            v-if="organism.link1"
            class="uppercase brand-window-button"
            :buttons="organism"
            :button-colors="organism.buttonColors ?? ['button-primary']"
          />
        </ClientOnly>
      </div>
      <div class="relative -order-1 lg:order-2">
        <Swiper
          :loop="true"
          :modules="modules"
          :pagination="true"
          :autoplay="
            organism?.sliderImages?.length > 1 ? autoplaySlider() : false
          "
          :navigation="{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }"
          :allowTouchMove="organism?.sliderImages?.length > 1"
          class="max-w-2xl lg:max-w-full"
        >
          <SwiperSlide
            v-for="(image, index) in organism.sliderImages"
            :key="'slider-image-' + index"
            class="select-none"
          >
            <figure class="w-full h-full">
              <img
                :class="imageClass(image)"
                class="w-full h-full max-h-[550px]"
                :src="getImageSrc(image, 'large')"
                :alt="image.alt"
                @load="setImageClass($event, image)"
                loading="lazy"
              />
            </figure>
          </SwiperSlide>
          <!-- Navigation buttons -->
          <div :class="buttonClass" class="swiper-button-next"></div>
          <div :class="buttonClass" class="swiper-button-prev"></div>
          <p
            v-if="organism?.disclaimer"
            class="block text-sm text-gray-500 pt-2 px-4 sm:px-0 mx-auto sm:mx-0 max-w-lg"
          >
            *The interior images are of a previously built home; some features
            may represent options or upgrades and are intended for illustrative
            purposes only.
          </p>
        </Swiper>
      </div>
    </div>
  </div>
</template>

<style>
.product-detail-brand-window {
  .tool-tip-button {
    button > div {
      @apply p-0;

      & > span {
        /* @apply w-3 h-3 text-[10px]; */
      }
    }
  }
  .brand-window-button a {
    @apply text-[15px] tracking-[0.6px] px-10 py-1.5;
  }

  .swiper-button-next.hidden,
  .swiper-button-prev.hidden {
    display: none !important;
  }

  .productDetailBWPrice_range {
    @apply mt-2;
  }
}
</style>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { HeartIcon as HeartIconSolid } from "@heroicons/vue/solid/index.js";
import { HeartIcon as HeartIconOutline } from "@heroicons/vue/outline/index.js";

import { storeToRefs } from "pinia";

const favoritesAuth = useFavoritesAuth();
const { isLoggedIn, userCookie } = storeToRefs(favoritesAuth);

favoritesAuth.refreshLoginStatus();

const favorites = useFavorites();
const { processingQueue } = storeToRefs(favorites);

const modalLogin = useModalLogin();
const { modalIsOpen } = storeToRefs(modalLogin);

const modules = [Navigation, Pagination, Autoplay];

const props = defineProps({
  organism: Object,
});

const autoplaySlider = () => {
  if (props.organism.autoplaySlider && props.organism.autoplayDelay) {
    return { delay: props.organism.autoplayDelay };
  }

  return false;
};

const buttonClass = computed(() => {
  if (props.organism && props.organism.sliderImages) {
    return props.organism.sliderImages.length > 1 ? "" : "hidden";
  }
  return "hidden"; // Default to 'hidden' if anything is undefined
});

const handleFavoriteClickProductDetailBW = (item) => {
  if (isLoggedIn.value) {
    toggleFavorite(item);
  } else if (!modalIsOpen.value) {
    modalLogin.openModal();
  }
};

// const isFavorite = ref(
//   favorites.isFavorite(props.organism?.id, userCookie.value?.favorites),
// );
const isFavorite = ref(
  isValidJSON(userCookie.value?.favorites) &&
    Array.isArray(JSON.parse(userCookie.value?.favorites))
    ? userCookie.value?.favorites.includes(props.organism?.id)
    : false,
);

const toggleFavorite = (item) => {
  const newFavoritesIds = favorites.toggleFavoriteProduct(item);

  isFavorite.value =
    isValidJSON(userCookie.value?.favorites) &&
    Array.isArray(JSON.parse(userCookie.value?.favorites))
      ? userCookie.value?.favorites.includes(props.organism?.id)
      : false;
};

const imageHeight = ref({});

const setImageClass = (event, image) => {
  imageHeight.value[image] = event.target.naturalHeight;
};

const imageClass = (image) =>
  imageHeight.value[image] > 800 ? "object-contain" : "object-cover";

onMounted(() => {
  props.organism?.sliderImages?.forEach((image) => {
    const img = new Image();
    img.src = getImageSrc(image, "large");
    img.onload = (event) => setImageClass(event, image);
  });
});
</script>
